import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, useCallback, useMemo, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

import { useAuth } from "../hooks/AuthProvider";
import DataGridTable from "../Components/DataGridTable";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";
import { LanguageContext } from "../context/LanguageContext";
import devTranslations from "../languages/device.json";

import { format } from "date-fns";
import BackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatastreamStatusDetails } from "./DatastreamStatusDetails";

import "../Components/modal.css";
import "./DeviceStatusLog.css";

const pagingoptions = [10, 20, 50, 100];
const UNIQUE_ID_FIELD = "device_status_log_id";
const EMPTY_STATUS_LOG_FILTERS = {
  status: "",
  dateFilterType: "",
  dateAt: "",
  dateFrom: "",
  dateTo: "",
};
const STATUS_COLOR_MAP = {
  perfect: "#4caf50",
  good: "#8bc34a",
  acceptable: "#ff9800",
  bad: "#f44336",
  default: "var(--primary)",
};
const normalizeStatusLogFilters = (filters = EMPTY_STATUS_LOG_FILTERS) => {
  const normalized = {
    status: filters?.status || "",
    dateFilterType: "",
    dateAt: "",
    dateFrom: "",
    dateTo: "",
  };

  if (filters?.dateFilterType === "at" && filters?.dateAt) {
    normalized.dateFilterType = "at";
    normalized.dateAt = filters.dateAt;
    return normalized;
  }

  if (filters?.dateFilterType === "between" && filters?.dateFrom && filters?.dateTo) {
    normalized.dateFilterType = "between";
    normalized.dateFrom = filters.dateFrom;
    normalized.dateTo = filters.dateTo;
  }

  return normalized;
};

export const DeviceStatusLog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { lang } = useContext(LanguageContext);

  const canViewStatusLog = permissions["view_devices"];
  const isRtl = ["ar", "ur"].includes(lang);

  const t = useMemo(
    () =>
      devTranslations?.[lang]?.deviceStatusLog || {
        title: "Device Status Log",
        noPermission: "You do not have permission to view device status logs.",
        noData: "No status log records found for this device.",
        status: "Status",
        createdAt: "Created At",
        deviceId: "Device DID",
        errorLoading: "Failed to load status logs:",
        perfect: "Perfect",
        good: "Good",
        acceptable: "Acceptable",
        bad: "Bad",
        detailsTitle: "Data Evaluation Details",
        evaluation: "Evaluation",
        datastreams: "Datastreams",
        priority: "Priority",
        high: "High",
        medium: "Medium",
        low: "Low",
        viewMode: "View",
        headerHint: "Read-only logs. Click a row to open details.",
        summaryTitle: "Log Viewer",
        summaryDesc: "This list is server-side paginated and sorted by created time.",
        back: "Back",
        filterStatus: "Status",
        filterDate: "Date Filter",
        filterDateAt: "At A Date",
        filterDateBetween: "Between Dates",
        filterApply: "Apply Filters",
        filterReset: "Reset Filters",
        filterAllStatuses: "All Statuses",
        filterAllDates: "All Dates",
        filterDateValue: "Date",
        filterStartDate: "Start Date",
        filterEndDate: "End Date",
        filterValidationAt: "Please choose a date for the date filter.",
        filterValidationBetween: "Please choose both start and end dates.",
        statsTitle: "Status Statistics",
        statsDesc: "Percentages are calculated from the full filtered result set.",
        statsCenterLabel: "Filtered Logs",
        statsEmpty: "No statistics available for the current filter.",
        totalLogs: "Total logs",
        totalPages: "Total pages",
      },
    [lang]
  );

  const [data, setData] = useState({ columns: [], rows: [], count: 0, loading: true });
  const [paginationState, setPaginationState] = useState({ page: 1, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: "created_at", sort: "desc" }]);
  const [totalPages, setTotalPages] = useState(1);
  const [deviceMeta, setDeviceMeta] = useState({ id: id || "", did: "", name: "" });
  const [filterDraft, setFilterDraft] = useState(EMPTY_STATUS_LOG_FILTERS);
  const [statusSummary, setStatusSummary] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [exportingExcel, setExportingExcel] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState({ open: false, statusLogId: null });

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right",
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({ open: true, type, message, position: isRtl ? "left" : "right" });
    },
    [isRtl]
  );

  const currentPage = paginationState.page;
  const currentPageSize = paginationState.pageSize;
  const currentSortField = sortModel[0]?.field || "created_at";
  const currentSortOrder = sortModel[0]?.sort?.toUpperCase() || "DESC";
  const getStatusColor = useCallback(
    (statusKey) => STATUS_COLOR_MAP[statusKey] || STATUS_COLOR_MAP.default,
    []
  );
  const formatPercentage = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0%";
    return `${numeric % 1 === 0 ? numeric.toFixed(0) : numeric.toFixed(1)}%`;
  }, []);
  const normalizedFilters = useMemo(
    () => normalizeStatusLogFilters(filterDraft),
    [filterDraft]
  );
  const updateFilterDraft = useCallback((updater) => {
    setFilterDraft((prev) => (typeof updater === "function" ? updater(prev) : updater));
    setPaginationState((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, []);
  const datePickerSlotProps = useMemo(
    () => ({
      textField: {
        className: "dsl-date-picker-field",
        fullWidth: true,
        placeholder: "DD/MM/YYYY",
        size: "small",
      },
      popper: {
        disablePortal: true,
        placement: isRtl ? "bottom-start" : "bottom-end",
        className: "dsl-date-picker-popper",
      },
      desktopPaper: {
        className: "dsl-date-picker-paper",
      },
      dialog: {
        className: "dsl-date-picker-dialog",
      },
    }),
    [isRtl]
  );
  const effectiveStatusOptions = useMemo(() => {
    const optionSet = new Set([filterDraft.status, ...statusOptions].filter(Boolean));
    return Array.from(optionSet);
  }, [filterDraft.status, statusOptions]);
  const visibleSummaryItems = useMemo(
    () => (Array.isArray(statusSummary) ? statusSummary.filter((item) => Number(item?.count) > 0) : []),
    [statusSummary]
  );
  const summaryGaugeBackground = useMemo(() => {
    if (visibleSummaryItems.length === 0) {
      return "conic-gradient(rgba(255,255,255,0.08) 0deg 360deg)";
    }

    let currentAngle = 0;
    const segments = visibleSummaryItems.map((item) => {
      const angle = Math.max(0, (Number(item?.percentage || 0) / 100) * 360);
      const startAngle = currentAngle;
      const endAngle = startAngle + angle;
      currentAngle = endAngle;
      return `${getStatusColor(item?.status_key)} ${startAngle}deg ${endAngle}deg`;
    });

    if (currentAngle < 360) {
      segments.push(`rgba(255,255,255,0.08) ${currentAngle}deg 360deg`);
    }

    return `conic-gradient(${segments.join(", ")})`;
  }, [getStatusColor, visibleSummaryItems]);

  const fetchData = useCallback(async () => {
    if (!canViewStatusLog || !id) {
      setData((prev) => ({ ...prev, loading: false }));
      return;
    }

    setData((prev) => ({ ...prev, loading: true }));

    try {
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: currentPageSize,
        sort: currentSortField,
        order: currentSortOrder,
      });

      if (normalizedFilters.status) {
        params.set("status", normalizedFilters.status);
      }

      if (normalizedFilters.dateFilterType === "at" && normalizedFilters.dateAt) {
        params.set("dateFilterType", "at");
        params.set("dateAt", normalizedFilters.dateAt);
      }

      if (normalizedFilters.dateFilterType === "between") {
        params.set("dateFilterType", "between");
        if (normalizedFilters.dateFrom) params.set("dateFrom", normalizedFilters.dateFrom);
        if (normalizedFilters.dateTo) params.set("dateTo", normalizedFilters.dateTo);
      }

      const res = await axios.get(apiUrl(`device/status-logs/${id}?${params.toString()}`));
      const responseData = res?.data?.data;

      if (!responseData || !Array.isArray(responseData.rows)) {
        throw new Error("Received an invalid data format from the server.");
      }

      const apiDeviceMeta = responseData?.device || {};
      setDeviceMeta({
        id: apiDeviceMeta?.id ?? id,
        did: apiDeviceMeta?.did || responseData?.rows?.[0]?.device_id || "",
        name: apiDeviceMeta?.name || responseData?.rows?.[0]?.device_name || "",
      });
      setStatusSummary(Array.isArray(responseData?.summary) ? responseData.summary : []);
      setStatusOptions(Array.isArray(responseData?.availableStatuses) ? responseData.availableStatuses : []);

      const formatDateTime = (input) => {
        const raw =
          input && typeof input === "object"
            ? input.value ?? input.row?.created_at ?? null
            : input;

        if (!raw) return "";

        try {
          const date = raw instanceof Date ? raw : new Date(raw);
          if (Number.isNaN(date.getTime())) return String(raw);
          return format(date, "dd/MM/yyyy HH:mm:ss");
        } catch {
          return String(raw);
        }
      };

      const columns = [
        {
          field: UNIQUE_ID_FIELD,
          headerName: "ID",
          width: 90,
          sortable: true,
        },
        {
          field: "device_id",
          headerName: t.deviceId,
          width: 260,
          align: "left",
          headerAlign: "left",
          sortable: false,
          renderCell: (params) => {
            const did = params?.row?.device_id || "";
            const deviceName = params?.row?.device_name || "";
            return deviceName ? `${deviceName} (${did})` : did;
          },
        },
        {
          field: "status",
          headerName: t.status,
          width: 150,
          align: "left",
          headerAlign: "left",
          sortable: true,
          renderCell: (params) => {
            const statusKey = String(
              params?.row?.status_key || params?.value || ""
            )
              .trim()
              .toLowerCase();
            const statusLabel =
              params?.row?.status_display || t[statusKey] || params?.value || "-";

            const statusColor =
              statusKey === "perfect"
                ? "#4caf50"
                : statusKey === "good"
                ? "#8bc34a"
                : statusKey === "acceptable"
                ? "#ff9800"
                : statusKey === "bad"
                ? "#f44336"
                : "var(--primary)";

            return (
              <span className="dsl-status-pill" style={{ color: statusColor }}>
                {statusLabel}
              </span>
            );
          },
        },
        {
          field: "priority",
          headerName: t.priority,
          width: 120,
          align: "left",
          headerAlign: "left",
          sortable: false,
          type: "number",
          renderCell: (params) => (
            <span className="dsl-priority-pill">{params?.value ?? "-"}</span>
          ),
        },
        {
          field: "created_at",
          headerName: t.createdAt,
          flex: 1,
          minWidth: 220,
          align: "left",
          headerAlign: "left",
          valueFormatter: (value) => formatDateTime(value),
        },
      ];

      setData({
        columns,
        rows: responseData.rows,
        count: responseData.count,
        loading: false,
      });

      setTotalPages(responseData.totalPages);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showAlert("error", `${t.errorLoading} ${errorMessage}`);
      setStatusSummary([]);
      setData({ columns: [], rows: [], count: 0, loading: false });
    }
  }, [
    id,
    canViewStatusLog,
    currentPage,
    currentPageSize,
    currentSortField,
    currentSortOrder,
    normalizedFilters.dateAt,
    normalizedFilters.dateFilterType,
    normalizedFilters.dateFrom,
    normalizedFilters.dateTo,
    normalizedFilters.status,
    showAlert,
    t,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buildStatusLogParams = useCallback(
    (extraParams = {}) => {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(currentPageSize),
        sort: currentSortField,
        order: currentSortOrder,
        ...extraParams,
      });

      if (normalizedFilters.status) {
        params.set("status", normalizedFilters.status);
      }

      if (normalizedFilters.dateFilterType === "at" && normalizedFilters.dateAt) {
        params.set("dateFilterType", "at");
        params.set("dateAt", normalizedFilters.dateAt);
      }

      if (normalizedFilters.dateFilterType === "between") {
        params.set("dateFilterType", "between");
        if (normalizedFilters.dateFrom) params.set("dateFrom", normalizedFilters.dateFrom);
        if (normalizedFilters.dateTo) params.set("dateTo", normalizedFilters.dateTo);
      }

      return params;
    },
    [
      currentPage,
      currentPageSize,
      currentSortField,
      currentSortOrder,
      normalizedFilters.dateAt,
      normalizedFilters.dateFilterType,
      normalizedFilters.dateFrom,
      normalizedFilters.dateTo,
      normalizedFilters.status,
    ]
  );

  const handleExportExcel = useCallback(async () => {
    if (!canViewStatusLog || !id || exportingExcel) return;

    setExportingExcel(true);
    try {
      const params = buildStatusLogParams({ export: "excel" });
      const response = await axios.get(apiUrl(`device/status-logs/${id}?${params.toString()}`), {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeDevice = String(deviceMeta?.did || id).replace(/[^a-z0-9_-]+/gi, "_");
      link.href = objectUrl;
      link.download = `device-status-logs-${safeDevice}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      showAlert("error", getErrorMessage(err));
    } finally {
      setExportingExcel(false);
    }
  }, [
    buildStatusLogParams,
    canViewStatusLog,
    deviceMeta?.did,
    exportingExcel,
    id,
    showAlert,
  ]);

  const handlePageChange = useCallback((newPage) => {
    setPaginationState((prev) => {
      if (newPage !== prev.page) return { ...prev, page: newPage };
      return prev;
    });
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPaginationState((prev) => {
      if (newPageSize !== prev.pageSize) return { page: 1, pageSize: newPageSize };
      return prev;
    });
  }, []);

  const handleSortModelChange = useCallback((newSortModel) => {
    setSortModel((prevSortModel) => {
      if (JSON.stringify(newSortModel) !== JSON.stringify(prevSortModel)) {
        setPaginationState((prev) => ({ ...prev, page: 1 }));
        return newSortModel;
      }
      return prevSortModel;
    });
  }, []);

  const handleRowClick = useCallback((params) => {
    const statusLogId = params.row?.[UNIQUE_ID_FIELD];
    if (statusLogId) setShowDetailsModal({ open: true, statusLogId });
  }, []);

  const resetFilters = useCallback(() => {
    setFilterDraft(EMPTY_STATUS_LOG_FILTERS);
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, []);

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  if (!canViewStatusLog) {
    return (
      <Fragment>
        <div id="modal" onClick={() => navigate(-1)}>
          <div
            id="popup"
            className={`dashboard-modal device-statuslog-modal ${isRtl ? "rtl" : ""}`}
            onClick={(e) => e.stopPropagation()}
            dir={isRtl ? "rtl" : "ltr"}
          >
            <div className="dashboard-header">
              <div className="dashboard-header-text">
                <h2>{t.title}</h2>
                <p>{t.noPermission}</p>
              </div>
            </div>

            <div className="modal-divider" />

            <div className="modal-content">
              <div className="dashboard-content device-statuslog-content">
                <div className="modal-info device-statuslog-info-banner">
                  <div className="device-statuslog-info-icon">i</div>
                  <div className="device-statuslog-info-text">
                    <h4>{t.accessRestricted || "Access Restricted"}</h4>
                    <p>{t.accessRestrictedDesc || "You need view_devices permission to open this page."}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer device-statuslog-footer">
              <button className="btn btn-cancel" onClick={() => navigate(-1)}>
                {devTranslations?.[lang]?.addDevice?.buttonCancel || "Close"}
              </button>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div id="modal" onClick={() => navigate(-1)}>
        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />

        {showDetailsModal.open && (
          <DatastreamStatusDetails
            statusLogId={showDetailsModal.statusLogId}
            close={() => setShowDetailsModal({ open: false, statusLogId: null })}
          />
        )}

        <div
          id="popup"
          className={`dashboard-modal device-statuslog-modal ${isRtl ? "rtl" : ""}`}
          onClick={(e) => e.stopPropagation()}
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-text">
              <h2>
                {t.title}{" "}
                <span className="dsl-sub">
                  (
                  {deviceMeta?.name
                    ? `${deviceMeta.name} (${deviceMeta.did || id})`
                    : `Device ID: ${id}`}
                  )
                </span>
              </h2>
              <p>
                {t.headerHint || "Read-only logs. Click a row to open details."}
              </p>
            </div>

            <button className="dsl-back-btn" onClick={() => navigate(-1)} title={t.back || "Back"}>
              <BackIcon />
            </button>
          </div>

          <div className="modal-divider" />

          <div className="modal-content">
            <div className="dashboard-content device-statuslog-content">
              <div className="modal-info device-statuslog-info-banner">
                <div className="device-statuslog-info-icon">i</div>
                <div className="device-statuslog-info-text">
                  <h4>{t.summaryTitle || "Log Viewer"}</h4>
                  <p>{t.summaryDesc || "Server-side pagination and sorting. Latest first by default."}</p>
                </div>
              </div>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="dsl-filters-shell">
                  <div className="dsl-filter-field">
                    <label className="dsl-filter-label">
                      {t.filterStatus || "Status"}
                    </label>
                    <select
                      className="dsl-filter-control"
                      value={filterDraft.status}
                      onChange={(e) =>
                        updateFilterDraft((prev) => ({ ...prev, status: e.target.value }))
                      }
                    >
                      <option value="">{t.filterAllStatuses || "All Statuses"}</option>
                      {effectiveStatusOptions.map((statusValue) => (
                        <option key={statusValue} value={statusValue}>
                          {statusValue}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="dsl-filter-field">
                    <label className="dsl-filter-label">
                      {t.filterDate || "Date Filter"}
                    </label>
                    <select
                      className="dsl-filter-control"
                      value={filterDraft.dateFilterType}
                      onChange={(e) =>
                        updateFilterDraft((prev) => ({
                          ...prev,
                          dateFilterType: e.target.value,
                          dateAt: e.target.value === "at" ? prev.dateAt : "",
                          dateFrom: e.target.value === "between" ? prev.dateFrom : "",
                          dateTo: e.target.value === "between" ? prev.dateTo : "",
                        }))
                      }
                    >
                      <option value="">{t.filterAllDates || "All Dates"}</option>
                      <option value="at">{t.filterDateAt || "At A Date"}</option>
                      <option value="between">{t.filterDateBetween || "Between Dates"}</option>
                    </select>
                  </div>

                  {filterDraft.dateFilterType === "at" ? (
                    <div className="dsl-filter-field">
                      <label className="dsl-filter-label">
                        {t.filterDateValue || "Date"}
                      </label>
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={filterDraft.dateAt ? dayjs(filterDraft.dateAt) : null}
                        onChange={(nextValue) =>
                          updateFilterDraft((prev) => ({
                            ...prev,
                            dateAt:
                              nextValue && nextValue.isValid()
                                ? nextValue.format("YYYY-MM-DD")
                                : "",
                          }))
                        }
                        slotProps={datePickerSlotProps}
                      />
                    </div>
                  ) : null}

                  {filterDraft.dateFilterType === "between" ? (
                    <Fragment>
                      <div className="dsl-filter-field">
                        <label className="dsl-filter-label">
                          {t.filterStartDate || "Start Date"}
                        </label>
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={filterDraft.dateFrom ? dayjs(filterDraft.dateFrom) : null}
                          onChange={(nextValue) =>
                            updateFilterDraft((prev) => {
                              const nextDateFrom =
                                nextValue && nextValue.isValid()
                                  ? nextValue.format("YYYY-MM-DD")
                                  : "";
                              const nextDateTo =
                                nextDateFrom &&
                                prev.dateTo &&
                                dayjs(prev.dateTo).isBefore(dayjs(nextDateFrom), "day")
                                  ? nextDateFrom
                                  : prev.dateTo;

                              return {
                                ...prev,
                                dateFrom: nextDateFrom,
                                dateTo: nextDateTo,
                              };
                            })
                          }
                          maxDate={filterDraft.dateTo ? dayjs(filterDraft.dateTo) : undefined}
                          slotProps={datePickerSlotProps}
                        />
                      </div>

                      <div className="dsl-filter-field">
                        <label className="dsl-filter-label">
                          {t.filterEndDate || "End Date"}
                        </label>
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={filterDraft.dateTo ? dayjs(filterDraft.dateTo) : null}
                          onChange={(nextValue) =>
                            updateFilterDraft((prev) => ({
                              ...prev,
                              dateTo:
                                nextValue && nextValue.isValid()
                                  ? nextValue.format("YYYY-MM-DD")
                                  : "",
                            }))
                          }
                          minDate={filterDraft.dateFrom ? dayjs(filterDraft.dateFrom) : undefined}
                          slotProps={datePickerSlotProps}
                        />
                      </div>
                    </Fragment>
                  ) : null}

                  <div className="dsl-filter-actions">
                    <button
                      className="btn btn-apply dsl-export-btn"
                      onClick={handleExportExcel}
                      disabled={exportingExcel || data.loading}
                      type="button"
                    >
                      <FileDownloadIcon fontSize="small" />
                      <span>{exportingExcel ? (t.exportingExcel || "Exporting...") : (t.exportExcel || "Export Excel")}</span>
                    </button>
                    <button className="btn btn-cancel-details" onClick={resetFilters}>
                      {t.filterReset || "Reset Filters"}
                    </button>
                  </div>
                </div>
              </LocalizationProvider>

              <div className="dsl-stats-panel">
                <div className="dsl-stats-copy">
                  <h4>{t.statsTitle || "Status Statistics"}</h4>
                  <p>{t.statsDesc || "Percentages are calculated from the full filtered result set."}</p>
                </div>

                <div className="dsl-stats-content">
                  <div className="dsl-summary-ring-shell">
                    <div
                      className="dsl-summary-ring"
                      style={{ background: summaryGaugeBackground }}
                    >
                      <div className="dsl-summary-ring-center">
                        <span className="dsl-summary-ring-label">
                          {t.statsCenterLabel || "Filtered Logs"}
                        </span>
                        <strong className="dsl-summary-ring-value">
                          {data.count}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="dsl-summary-legend">
                    {visibleSummaryItems.length > 0 ? (
                      visibleSummaryItems.map((item) => (
                        <div
                          key={`${item.status}-${item.status_key}`}
                          className="dsl-summary-legend-item"
                        >
                          <span
                            className="dsl-summary-legend-swatch"
                            style={{ backgroundColor: getStatusColor(item.status_key) }}
                          />
                          <div className="dsl-summary-legend-text">
                            <strong>{item.status_display || item.status}</strong>
                            <span>{item.count} - {formatPercentage(item.percentage)}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="dsl-summary-empty">
                        {t.statsEmpty || "No statistics available for the current filter."}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dsl-table-area">
                {data.loading && data.rows.length === 0 ? (
                  <Spinner />
                ) : data.rows.length > 0 ? (
                  <div className="table-wrapper dsl-table-wrapper">
                    <DataGridTable
                      columns={data.columns}
                      rows={data.rows}
                      idcolumn={UNIQUE_ID_FIELD}
                      pagingoptions={pagingoptions}
                      checkboxSelection={false}
                      showToolbar={false}
                      showHeader={false}
                      tableKey="deviceStatusLog"
                      rowSelectionModel={rowSelectionModel}
                      setRowSelectionModel={setRowSelectionModel}
                      handleRowClick={handleRowClick}
                      loading={data.loading}
                      pagination={{
                        page: currentPage,
                        pageSize: currentPageSize,
                        count: data.count,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        sortModel: sortModel,
                        onSortModelChange: handleSortModelChange,
                        serverSide: true,
                      }}
                    />
                  </div>
                ) : (
                  <div className="dsl-empty">{t.noData}</div>
                )}
              </div>

              <div className="dsl-meta">
                {(t.totalLogs || "Total logs")}: {data.count} - {(t.totalPages || "Total pages")}: {totalPages}
              </div>
            </div>
          </div>

          <div className="modal-footer device-statuslog-footer">
            <div className="device-statuslog-footer-left">
              <button className="btn btn-apply" onClick={fetchData}>Refresh</button>
            </div>

            <div className="device-statuslog-footer-right">
              <button className="btn btn-cancel" onClick={() => navigate(-1)}>
                {devTranslations?.[lang]?.addDevice?.buttonCancel || "Close"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
